/**
 * Companion-mode route scope (U-12). The store builds show the product and the pages a reviewer must reach
 * (sign-in, legal, support, account deletion, shared media kits and drafts) and nothing else: no marketing
 * pages, no invite-request `/verify`, no pricing, no referrals, no internal tools. Anything outside the
 * allowlist goes to the dashboard (or to sign-in when signed out).
 *
 * Path based, not name based, so routes added later (for example `/support` and `/delete-account`) are
 * covered by their path alone and a new marketing route is blocked by default.
 */

/** Exact paths a companion build may open. */
const EXACT_PATHS = new Set([
    '/native-login',
    '/login',
    '/logout',
    '/reset-password',
    '/privacy',
    '/terms',
    '/support',
    '/delete-account',
    '/oauth-callback',
    '/auth/callback',
])

/** Path prefixes (the prefix itself and everything below it). */
const PREFIXES = ['/dashboard', '/media-kit', '/draft']

/** Dashboard sections that sell or pay out: referrals ("Get $100", Stripe Connect payouts). */
const BLOCKED_DASHBOARD_SECTIONS = new Set(['referrals'])

function normalizePath(path: string): string {
    const bare = (path || '/').split(/[?#]/)[0] || '/'
    const trimmed = bare.length > 1 ? bare.replace(/\/+$/, '') : bare
    return trimmed.toLowerCase()
}

function underPrefix(path: string, prefix: string): boolean {
    return path === prefix || path.startsWith(`${prefix}/`)
}

/** True when a companion build may show this path. */
export function isCompanionAllowedPath(path: string): boolean {
    const p = normalizePath(path)
    if (EXACT_PATHS.has(p)) return true
    if (underPrefix(p, '/dashboard')) {
        const section = p.split('/')[2]
        return !section || !BLOCKED_DASHBOARD_SECTIONS.has(section)
    }
    return PREFIXES.some((prefix) => underPrefix(p, prefix))
}

export type CompanionRedirect = { name: 'dashboard' } | { name: 'native-login' }

/**
 * Where a companion build sends a navigation, or null to let it through. Signed in → the dashboard,
 * signed out → sign-in.
 */
export function companionRedirectFor(path: string, isAuthenticated: boolean): CompanionRedirect | null {
    if (isCompanionAllowedPath(path)) return null
    return isAuthenticated ? { name: 'dashboard' } : { name: 'native-login' }
}
